import { useEffect, type DependencyList, type RefObject } from "react";

/** Cap product media height; white fill only when this cap is engaged. */
export const PRODUCT_CARD_MAX_HEIGHT_PX = 300;

/** White background when intrinsic height exceeds the max-height cap (viewport-stable). */
export function syncProductCardMaxHeightBg(
  frame: HTMLElement | null,
  img: HTMLImageElement | null,
  maxH = PRODUCT_CARD_MAX_HEIGHT_PX,
) {
  if (!frame || !img?.naturalWidth || !img.naturalHeight) return;
  frame.classList.toggle("is-max-h", img.naturalHeight > maxH + 0.5);
}

/** Apply product-card max-height bg once per mount / media deps (not on resize). */
export function useProductCardMaxHeightBg(
  frameRef: RefObject<HTMLElement | null>,
  active: boolean,
  deps: DependencyList,
) {
  useEffect(() => {
    if (!active) return;
    const frame = frameRef.current;
    if (!frame) return;
    const sync = () => {
      const img = frame.querySelector("img") as HTMLImageElement | null;
      syncProductCardMaxHeightBg(frame, img);
    };
    sync();
    // Cached images often skip `onLoad` after remount / bfcache restore — re-sync
    // so the white max-height fill doesn't vanish when returning from a shop tab.
    const img = frame.querySelector("img") as HTMLImageElement | null;
    if (img && !img.complete) {
      img.addEventListener("load", sync);
    }
    const onShow = () => {
      if (document.visibilityState && document.visibilityState !== "visible") {
        return;
      }
      sync();
    };
    window.addEventListener("pageshow", onShow);
    document.addEventListener("visibilitychange", onShow);
    return () => {
      img?.removeEventListener("load", sync);
      window.removeEventListener("pageshow", onShow);
      document.removeEventListener("visibilitychange", onShow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller passes explicit deps
  }, deps);
}
